import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  Bot,
  Brain,
  CheckCircle2,
  Cloud,
  Cpu,
  Database,
  HardDrive,
  Network,
  RefreshCw,
  Server,
  Shield,
  Workflow,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SERVICES = [
  { id: 'docker', name: 'Docker', description: 'Conteneurs et services', icon: Server, status: 'unknown' },
  { id: 'n8n', name: 'n8n', description: 'Automatisations et workflows', icon: Workflow, status: 'unknown' },
  { id: 'baserow', name: 'Baserow', description: 'Données métier DNCM', icon: Database, status: 'unknown' },
  { id: 'ollama', name: 'Ollama', description: 'Moteurs LLM locaux', icon: Brain, status: 'unknown' },
  { id: 'litellm', name: 'LiteLLM', description: 'Routeur multi-LLM', icon: Network, status: 'unknown' },
  { id: 'dolphin', name: 'Dolphin', description: 'Selena locale privée', icon: Bot, status: 'unknown' },
  { id: 'deepseek', name: 'DeepSeek', description: 'Moteur spécialisé', icon: Cpu, status: 'standby' },
  { id: 'groq', name: 'Groq', description: 'Cloud rapide sur autorisation', icon: Cloud, status: 'standby' }
];

const statusClasses = {
  online: 'text-[#00FF9D] border-[#00FF9D]/30 bg-[#00FF9D]/5',
  operational: 'text-[#00FF9D] border-[#00FF9D]/30 bg-[#00FF9D]/5',
  standby: 'text-[#FFD700] border-[#FFD700]/30 bg-[#FFD700]/5',
  warning: 'text-[#FFD700] border-[#FFD700]/30 bg-[#FFD700]/5',
  offline: 'text-[#DC143C] border-[#DC143C]/30 bg-[#DC143C]/5',
  error: 'text-[#DC143C] border-[#DC143C]/30 bg-[#DC143C]/5',
  unknown: 'text-[#888888] border-white/10 bg-white/[0.02]'
};

const normalizeStatus = (value) => {
  const status = String(value || 'unknown').toLowerCase();

  if (['ok', 'healthy', 'active', 'running', 'up'].includes(status)) return 'online';
  if (['inactive', 'down', 'failed'].includes(status)) return 'offline';

  return status;
};

export default function CommandCenterModule({ token }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [system, setSystem] = useState({
    status: 'unknown',
    cpu: 0,
    memory: 0,
    disk: 0,
    alerts: 0,
    uptime: '—',
    health_score: 0,
    vps_ip: '—'
  });
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [events, setEvents] = useState([]);
  const [diagnosticRunning, setDiagnosticRunning] = useState(false);

  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  const addEvent = (message, level = 'info') => {
    setEvents((current) => [
      {
        id: `${Date.now()}-${Math.random()}`,
        time: new Date().toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
        message,
        level
      },
      ...current
    ].slice(0, 20));
  };

  const runVpsDiagnostic = async () => {
    if (diagnosticRunning) return;
    setDiagnosticRunning(true);
    addEvent("Diagnostic VPS lancé...");
    try {
      const response = await axios.post(`${API}/command-center/diagnostic`, {}, { headers: authHeaders });
      addEvent(response.data?.message || "Diagnostic VPS terminé.");
    } catch (error) {
      addEvent(`Échec du diagnostic VPS : ${error.response?.data?.message || error.message}`, "warning");
    } finally {
      setDiagnosticRunning(false);
    }
  };

  const loadCommandCenter = async (manual = false) => {
    if (manual) setRefreshing(true);

    try {
      const requests = await Promise.allSettled([
        axios.get(`${API}/health`, { headers: authHeaders }),
        axios.get(`${API}/ecosystem/status`, { headers: authHeaders }),
        axios.get(`${API}/command-center/status`, { headers: authHeaders })
      ]);

      const health =
        requests[0].status === 'fulfilled'
          ? requests[0].value.data
          : {};

      const ecosystem =
        requests[1].status === 'fulfilled'
          ? requests[1].value.data
          : {};

      const command =
        requests[2].status === 'fulfilled'
          ? requests[2].value.data
          : {};

      setSystem({
        status: normalizeStatus(
          command.status ||
          ecosystem.vps_status ||
          health.status ||
          'unknown'
        ),
        cpu: Number(
          command.cpu ??
          command.avg_cpu ??
          ecosystem.avg_cpu ??
          0
        ),
        memory: Number(
          command.memory ??
          command.avg_memory ??
          ecosystem.avg_memory ??
          0
        ),
        disk: Number(command.disk ?? command.disk_usage ?? 0),
        alerts: Number(command.alerts ?? 0),
        uptime: command.uptime || '—',
        health_score: Math.max(0, Math.round(100 - ((Number(command.cpu ?? 0) + Number(command.memory ?? 0) + Number(command.disk ?? 0)) / 6))),
        vps_ip: command.vps_ip || ecosystem.vps_ip || '—'
      });

      const receivedServices = Array.isArray(command.services)
        ? command.services
        : [];

      setServices(
        DEFAULT_SERVICES.map((service) => {
          const match = receivedServices.find(
            (item) =>
              item.id === service.id ||
              String(item.name || '').toLowerCase() === service.name.toLowerCase()
          );

          return match
            ? {
                ...service,
                ...match,
                status: normalizeStatus(match.status)
              }
            : service;
        })
      );

      addEvent(
        manual
          ? 'Actualisation manuelle terminée.'
          : 'Command Center synchronisé.'
      );
    } catch (error) {
      console.error('Command Center error:', error);
      addEvent('Certaines données temps réel sont indisponibles.', 'warning');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadCommandCenter();

    const interval = setInterval(() => {
      loadCommandCenter();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const statusCard = (label, value, Icon, suffix = '') => (
    <div className="stat-card card-hover corner-accent">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2 border border-white/10 bg-white/[0.03]">
          <Icon className="w-5 h-5 text-[#00BFFF]" />
        </div>
      </div>

      <p className="stat-value">
        {value}{suffix}
      </p>

      <p className="stat-label">{label}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-mono text-sm text-[#DC143C] animate-pulse">
          INITIALIZING SM-002 COMMAND CENTER...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="command-center-module" className="space-y-6 animate-fade-in">

      <section className="grid-card p-6 border-l-2 border-l-[#00FF9D]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-6 h-6 text-[#00FF9D]" />

              <h2 className="font-heading text-2xl font-bold text-[#EDEDED] uppercase tracking-wider">
                SM-002 Command Center
              </h2>
            </div>

            <p className="font-mono text-sm text-[#888888]">
              Supervision privée de Selena Matrix · VPS {system.vps_ip}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className={`px-4 py-2 border font-mono text-xs uppercase ${statusClasses[system.status] || statusClasses.unknown}`}>
              ● {system.status}
            </div>

            <button
              type="button"
              onClick={() => loadCommandCenter(true)}
              disabled={refreshing}
              className="btn-secondary px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              ACTUALISER
            </button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
        {statusCard('CHARGE CPU', system.cpu, Cpu, '%')}
        {statusCard('MÉMOIRE', system.memory, Activity, '%')}
        {statusCard('DISQUE', system.disk, HardDrive, '%')}
        {statusCard('UPTIME', system.uptime, Server)}
        {statusCard('SANTÉ SYSTÈME', system.health_score, Shield, '%')}
        {statusCard('ALERTES', system.alerts, AlertTriangle)}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        <div className="xl:col-span-2 grid-card">
          <div className="module-header flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-[#DC143C]" />
              <span>ORGANES CONNECTÉS</span>
            </div>

            <span className="font-mono text-[10px] text-[#888888]">
              AUTO REFRESH 30S
            </span>
          </div>

          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {services.map((service) => {
              const Icon = service.icon;
              const style = statusClasses[service.status] || statusClasses.unknown;

              return (
                <article
                  key={service.id}
                  className={`border p-4 transition-all hover:bg-white/[0.03] ${style}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 mt-0.5" />

                      <div>
                        <h3 className="font-heading text-sm font-bold text-[#EDEDED] uppercase">
                          {service.name}
                        </h3>

                        <p className="font-mono text-[11px] text-[#888888] mt-1">
                          {service.description}
                        </p>
                      </div>
                    </div>

                    <span className="font-mono text-[10px] uppercase">
                      {service.status}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className="space-y-6">

          <section className="grid-card">
            <div className="module-header flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#9D00FF]" />
              SELENA ACTIVE
            </div>

            <div className="p-5 space-y-4">
              <div>
                <p className="font-mono text-[10px] text-[#888888] uppercase">
                  Mode
                </p>

                <p className="font-heading text-lg text-[#00FF9D]">
                  LOCAL PRIVÉ
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="border border-white/10 p-3">
                  <p className="font-mono text-[10px] text-[#888888]">MOTEUR</p>
                  <p className="font-heading text-sm text-[#EDEDED]">DOLPHIN</p>
                </div>

                <div className="border border-white/10 p-3">
                  <p className="font-mono text-[10px] text-[#888888]">ROUTEUR</p>
                  <p className="font-heading text-sm text-[#EDEDED]">LITELLM</p>
                </div>

                <div className="border border-white/10 p-3">
                  <p className="font-mono text-[10px] text-[#888888]">MÉMOIRE</p>
                  <p className="font-heading text-sm text-[#00FF9D]">ACTIVE</p>
                </div>

                <div className="border border-white/10 p-3">
                  <p className="font-mono text-[10px] text-[#888888]">CLOUD</p>
                  <p className="font-heading text-sm text-[#FFD700]">CONTRÔLÉ</p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid-card">
            <div className="module-header flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#FFD700]" />
              COMMANDES RAPIDES
            </div>

            <div className="p-4 grid gap-2">
              {[
                'DIAGNOSTIC VPS',
                'VÉRIFIER DOCKER',
                'VÉRIFIER LES LLM',
                'CRÉER UN BACKUP'
              ].map((label) => (
                <button
                  key={label}
                  type="button"
                  disabled={label === 'DIAGNOSTIC VPS' && diagnosticRunning}
                    onClick={() => label === 'DIAGNOSTIC VPS' ? runVpsDiagnostic() : addEvent(`${label} demandé en mode sécurisé.`)}
                  className="btn-secondary py-3 text-xs text-left px-4"
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        </div>
      </section>

      <section className="grid-card">
        <div className="module-header flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#00BFFF]" />
          ACTIVITÉ LIVE
        </div>

        <div className="divide-y divide-white/5">
          {events.length === 0 ? (
            <div className="p-5 font-mono text-xs text-[#888888]">
              Aucun événement enregistré.
            </div>
          ) : (
            events.map((event) => (
              <div
                key={event.id}
                className="p-4 flex items-start gap-4"
              >
                {event.level === 'warning' ? (
                  <AlertTriangle className="w-4 h-4 text-[#FFD700] mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-[#00FF9D] mt-0.5" />
                )}

                <span className="font-mono text-[10px] text-[#00BFFF]">
                  {event.time}
                </span>

                <p className="font-mono text-xs text-[#B8B8B8]">
                  {event.message}
                </p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
